import { useCallback, useState } from 'react';
import {
  PublicKey,
  TransactionInstruction,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js';
import {
  claimOreInstruction,
  claimSolInstruction,
} from '../lib/evore';
import { getConnection } from '../lib/rpc';
import type { UnifiedWallet } from '../auth/useWallet';

export type LegacyClaimStep =
  | 'idle'
  | 'preparing'
  | 'signing'
  | 'submitting'
  | 'confirming'
  | 'success'
  | 'error';

export interface UseLegacyClaimResult {
  step: LegacyClaimStep;
  error: string | null;
  signature: string | null;
  /** Claim pending refined ORE from the wallet's OreMiner PDA into its ORE ATA. */
  claimOre: () => Promise<void>;
  /** Sweep pending SOL from the wallet's OreMiner PDA into the wallet itself. */
  claimSol: () => Promise<void>;
  reset: () => void;
}

/**
 * Legacy-miner claim flow. Unlike `useWithdraw` (which targets Evore
 * subaccounts via the `mm*` instruction wrappers), this hook targets the
 * ORE program directly — the connected wallet IS the OreMiner authority.
 *
 * No checkpoint preamble (see memory `legacy-ore-mining-mechanics`):
 * legacy miners haven't been mining recently, so there's no
 * pending-round-not-yet-checkpointed scenario for `claim_sol` to worry
 * about. `claim_ore` is similarly standalone — the on-chain program
 * folds in the live refining via the treasury factor itself.
 */
export function useLegacyClaim(wallet: UnifiedWallet): UseLegacyClaimResult {
  const [step, setStep] = useState<LegacyClaimStep>('idle');
  const [error, setError] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);

  const reset = useCallback(() => {
    setStep('idle');
    setError(null);
    setSignature(null);
  }, []);

  const sendInstruction = useCallback(
    async (instruction: TransactionInstruction, signer: PublicKey) => {
      const sign = wallet.signTransaction;
      if (!sign) throw new Error('sunset.errors.walletNotConnected');
      const connection = getConnection();

      const { blockhash, lastValidBlockHeight } =
        await connection.getLatestBlockhash('confirmed');

      const message = new TransactionMessage({
        payerKey: signer,
        recentBlockhash: blockhash,
        instructions: [instruction],
      }).compileToV0Message();

      const tx = new VersionedTransaction(message);

      setStep('signing');
      const signed = await sign(tx);

      setStep('submitting');
      const sig = await connection.sendTransaction(signed, {
        skipPreflight: false,
        maxRetries: 3,
      });
      setSignature(sig);

      setStep('confirming');
      const confirmation = await connection.confirmTransaction(
        { signature: sig, blockhash, lastValidBlockHeight },
        'confirmed'
      );
      if (confirmation.value.err) {
        throw new Error(
          `Transaction failed: ${JSON.stringify(confirmation.value.err)}`
        );
      }
    },
    [wallet]
  );

  const claimOre = useCallback(async () => {
    const signer = wallet.publicKey;
    if (!signer) {
      setError('sunset.errors.walletNotConnected');
      setStep('error');
      return;
    }
    try {
      setError(null);
      setSignature(null);
      setStep('preparing');
      await sendInstruction(claimOreInstruction(signer), signer);
      setStep('success');
    } catch (err) {
      console.error('[useLegacyClaim] claimOre failed:', err);
      setError(err instanceof Error ? err.message : 'sunset.errors.claimOreFailed');
      setStep('error');
    }
  }, [wallet, sendInstruction]);

  const claimSol = useCallback(async () => {
    const signer = wallet.publicKey;
    if (!signer) {
      setError('sunset.errors.walletNotConnected');
      setStep('error');
      return;
    }
    try {
      setError(null);
      setSignature(null);
      setStep('preparing');
      await sendInstruction(claimSolInstruction(signer), signer);
      setStep('success');
    } catch (err) {
      console.error('[useLegacyClaim] claimSol failed:', err);
      setError(err instanceof Error ? err.message : 'sunset.errors.claimSolFailed');
      setStep('error');
    }
  }, [wallet, sendInstruction]);

  return { step, error, signature, claimOre, claimSol, reset };
}
